import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AerodromePermit, AerodromePermitClass, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const INSPECTOR_ROLES = ['FLIGHT_OPS_INSPECTOR'];
const PERMIT_CLASSES: AerodromePermitClass[] = ['INTERNATIONAL', 'DOMESTIC', 'AIRSTRIP'];
const STATES = ['APPLIED', 'UNDER_REVIEW', 'INSPECTION', 'FEE_PENDING', 'ACTIVE', 'SUSPENDED', 'REVOKED', 'EXPIRED', 'REJECTED'];
const CHECKLIST_ITEMS = ['Runway surface & markings', 'Lighting systems', 'Fire & rescue cover', 'Safety management system', 'Obstacle-free approach/departure surfaces'];

/** M23 — aerodrome operator permits: Mahe (international) / Praslin (domestic) / La Digue (airstrip). */
export function AerodromePermits() {
  const { persona } = useAuth();
  const isInspector = persona ? persona.roles.some((r) => INSPECTOR_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showApply, setShowApply] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m23/aerodrome-permits?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<AerodromePermit>>(path);

  return (
    <div>
      <PageHeader
        title="Aerodrome Permits"
        subtitle="Aerodrome operator permits — application, review, on-site inspection, fee, activation (M23 — SCAA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-44" placeholder="Permit no. or name…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All states</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {isInspector ? (
              <button className="btn-primary" onClick={() => setShowApply(true)}>
                + Apply for permit
              </button>
            ) : null}
          </>
        }
      />

      {!isInspector ? <RoleNotice roles={INSPECTOR_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No aerodrome permits match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Permit no.</th>
                <th className="table-head">Aerodrome</th>
                <th className="table-head">Class</th>
                <th className="table-head">Valid to</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{p.permitNo}</td>
                  <td className="table-cell">{p.aerodromeName}</td>
                  <td className="table-cell text-xs text-slate-500">{p.permitClass}</td>
                  <td className="table-cell text-xs text-slate-500">{p.validTo ? formatDate(p.validTo) : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showApply ? (
        <ApplyAerodromePermitModal
          onClose={() => setShowApply(false)}
          onCreated={() => {
            setShowApply(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <AerodromePermitDetailModal
          id={selectedId}
          canAct={isInspector}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function ApplyAerodromePermitModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [operatorType, setOperatorType] = useState<'PERSON' | 'ENTITY'>('ENTITY');
  const [operatorId, setOperatorId] = useState('');
  const [aerodromeName, setAerodromeName] = useState('');
  const [icaoCode, setIcaoCode] = useState('');
  const [permitClass, setPermitClass] = useState<AerodromePermitClass>('DOMESTIC');
  const [insurancePolicyRef, setInsurancePolicyRef] = useState('');
  const [insuranceExpiresAt, setInsuranceExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m23/aerodrome-permits', {
        operatorType,
        operatorId: operatorId.trim(),
        aerodromeName: aerodromeName.trim(),
        ...(icaoCode.trim() ? { icaoCode: icaoCode.trim().toUpperCase() } : {}),
        permitClass,
        insurancePolicyRef: insurancePolicyRef.trim(),
        insuranceExpiresAt: new Date(insuranceExpiresAt).toISOString(),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to apply');
    } finally {
      setBusy(false);
    }
  };

  const valid = operatorId.trim() && aerodromeName.trim().length >= 2 && insurancePolicyRef.trim() && insuranceExpiresAt;

  return (
    <Modal title="Apply for an aerodrome operator permit" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Operator type">
          <select className="field-input" value={operatorType} onChange={(e) => setOperatorType(e.target.value as 'PERSON' | 'ENTITY')}>
            <option value="ENTITY">Entity</option>
            <option value="PERSON">Person</option>
          </select>
        </Field>
        <Field label="Operator id" className="col-span-2">
          <input className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} />
        </Field>
        <Field label="Aerodrome name">
          <input className="field-input" value={aerodromeName} onChange={(e) => setAerodromeName(e.target.value)} placeholder="Praslin Airport" />
        </Field>
        <Field label="ICAO code (optional)">
          <input className="field-input" value={icaoCode} onChange={(e) => setIcaoCode(e.target.value)} placeholder="FSPP" />
        </Field>
        <Field label="Permit class">
          <select className="field-input" value={permitClass} onChange={(e) => setPermitClass(e.target.value as AerodromePermitClass)}>
            {PERMIT_CLASSES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Insurance policy ref">
          <input className="field-input" value={insurancePolicyRef} onChange={(e) => setInsurancePolicyRef(e.target.value)} />
        </Field>
        <Field label="Insurance expires">
          <input className="field-input" type="date" value={insuranceExpiresAt} onChange={(e) => setInsuranceExpiresAt(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Applying…' : 'Submit application'}
      </button>
    </Modal>
  );
}

function AerodromePermitDetailModal({ id, canAct, onClose, onChanged }: { id: string; canAct: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<AerodromePermit>(`/api/v1/m23/aerodrome-permits/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [inspectionDate, setInspectionDate] = useState('');
  const [inspectorId, setInspectorId] = useState('');
  const [result, setResult] = useState<'PASS' | 'FAIL'>('PASS');
  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => Object.fromEntries(CHECKLIST_ITEMS.map((i) => [i, true])));
  const [failReasons, setFailReasons] = useState('');
  const [showAction, setShowAction] = useState<string | null>(null);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const p = q.data;

  return (
    <Modal title={p ? `${p.permitNo} — ${p.aerodromeName}` : 'Aerodrome permit'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={p.status} />} />
            <Detail label="Class" value={p.permitClass} />
            <Detail label="ICAO code" value={p.icaoCode ?? '—'} />
            <Detail label="Operator" value={p.operatorId} mono />
            <Detail label="Insurance ref" value={p.insurancePolicyRef} />
            <Detail label="Valid to" value={p.validTo ? formatDate(p.validTo) : '—'} />
          </div>
          {p.rejectionReason ? <ErrorNote message={`Rejected: ${p.rejectionReason}`} /> : null}
          {p.suspendedReason ? <ErrorNote message={`Suspended: ${p.suspendedReason}`} /> : null}
          {p.inspectionDate ? (
            <Detail
              label="Last inspection"
              value={`${formatDate(p.inspectionDate)} — ${p.inspectionResult ?? 'pending'} (inspector ${p.inspectorId ?? '—'})`}
            />
          ) : null}

          {canAct ? (
            <div className="space-y-3 rounded-lg border border-slate-200 p-3">
              {p.status === 'APPLIED' ? (
                <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m23/aerodrome-permits/${p.id}/review`))}>
                  {busy ? 'Moving…' : 'Move to review'}
                </button>
              ) : null}

              {p.status === 'UNDER_REVIEW' ? (
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Inspection date">
                    <input className="field-input" type="date" value={inspectionDate} onChange={(e) => setInspectionDate(e.target.value)} />
                  </Field>
                  <Field label="Inspector id">
                    <input className="field-input" value={inspectorId} onChange={(e) => setInspectorId(e.target.value)} />
                  </Field>
                  <button
                    className="btn-primary col-span-2"
                    disabled={busy || !inspectionDate || !inspectorId.trim()}
                    onClick={() =>
                      call(() =>
                        api.post(`/api/v1/m23/aerodrome-permits/${p.id}/schedule-inspection`, {
                          inspectionDate: new Date(inspectionDate).toISOString(),
                          inspectorId: inspectorId.trim(),
                        }),
                      )
                    }
                  >
                    {busy ? 'Scheduling…' : 'Schedule on-site inspection'}
                  </button>
                </div>
              ) : null}

              {p.status === 'INSPECTION' ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {CHECKLIST_ITEMS.map((item) => (
                      <label key={item} className="flex items-center gap-2 text-xs text-slate-600">
                        <input type="checkbox" checked={checklist[item] ?? true} onChange={(e) => setChecklist((c) => ({ ...c, [item]: e.target.checked }))} />
                        {item}
                      </label>
                    ))}
                  </div>
                  <Field label="Result">
                    <select className="field-input" value={result} onChange={(e) => setResult(e.target.value as 'PASS' | 'FAIL')}>
                      <option value="PASS">PASS</option>
                      <option value="FAIL">FAIL</option>
                    </select>
                  </Field>
                  {result === 'FAIL' ? (
                    <Field label="Fail reasons (comma-separated)">
                      <input className="field-input" value={failReasons} onChange={(e) => setFailReasons(e.target.value)} />
                    </Field>
                  ) : null}
                  <button
                    className={result === 'PASS' ? 'btn-success' : 'btn-danger'}
                    disabled={busy}
                    onClick={() =>
                      call(() =>
                        api.post(`/api/v1/m23/aerodrome-permits/${p.id}/inspection-result`, {
                          result,
                          checklist,
                          failReasons: result === 'FAIL' ? failReasons.split(',').map((s) => s.trim()).filter(Boolean) : [],
                        }),
                      )
                    }
                  >
                    {busy ? 'Recording…' : `Record ${result}`}
                  </button>
                </div>
              ) : null}

              {p.status === 'ACTIVE' ? (
                showAction === 'suspend' ? (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => call(() => api.post(`/api/v1/m23/aerodrome-permits/${p.id}/suspend`, { reason: reason.trim() }))}>
                      Confirm
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('suspend')}>
                    Suspend
                  </button>
                )
              ) : null}

              {p.status === 'SUSPENDED' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m23/aerodrome-permits/${p.id}/reinstate`))}>
                  Reinstate
                </button>
              ) : null}

              {p.status === 'ACTIVE' || p.status === 'SUSPENDED' ? (
                showAction === 'revoke' ? (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => call(() => api.post(`/api/v1/m23/aerodrome-permits/${p.id}/revoke`, { reason: reason.trim() }))}>
                      Confirm
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('revoke')}>
                    Revoke
                  </button>
                )
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={INSPECTOR_ROLES} />
          )}
        </div>
      ) : null}
    </Modal>
  );
}
